import React, { useState, useContext, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import { AppAlert } from '../../services/AppAlert';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';
import useLocation from '../../hooks/useLocation';
import { useAppSelector } from '../../redux/helper';
import { s, iconSize, fs, v, borderWidth } from '../../utils/libs';
import { isEmployeeAdminRole } from '../../utils/roles';
import { formatHeaderLocationName } from '../../utils/headerLocationName';
import { useNavigation } from '@react-navigation/native';
import ErrorContext from '../../Context/ErrorContext';
import { GTFonts } from '../../theme';
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import LocationBottomSheet from './LocationBottomSheet';
import { LocationSheetFooter, LocationSheetList } from './LocationSheetList';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../redux/Store';

import {
  clearEmployeeLocationState,
  clearOwnerLocationState,
} from '../../services/locationSwitchCleanup';
import {
  setIsAllLocations,
  setLocationSwitching,
  incrementLocationVersion,
} from '../../redux/locationSlice';
import { selectLocationPickerOpenRequest } from '../../redux/uiSlice';

interface LocationPickerProps {
  onLocationChange?: (locationId: string) => void;
  variant?: 'default' | 'compact';
  textColor?: string; // used for compact variant in headers
  showAllLocationsOption?: boolean; // Show "All Locations" option for backoffice aggregate queries
}

const LocationPicker: React.FC<LocationPickerProps> = ({
  onLocationChange,
  variant = 'default',
  textColor,
  showAllLocationsOption = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const isAllLocations = useAppSelector(state => state.location.isAllLocations);
  const currentLocationData = useAppSelector(
    state => state.location.currentLocation,
  );
  const employeedata = useAppSelector(
    state => state.auth.inApp.account.employeeInfo,
  );
  const currentLocationId = useAppSelector(
    state => state.location.currentLocationId,
  );
  const { locations, isLocationSwitching, switchLocation, isAdmin } =
    useLocation();

  const [modalVisible, setModalVisible] = useState(false);
  const bottomSheetRef = useRef<BottomSheetModal | null>(null);
  const navigation = useNavigation<any>();
  const { handleError } = useContext(ErrorContext);

  // Imperative open from the activation tracker's "Visit test location" task.
  // Only the header (compact, admin) instance should respond, so it doesn't
  // double-present when multiple pickers are mounted.
  const openRequest = useAppSelector(selectLocationPickerOpenRequest);
  const lastHandledOpenRequest = useRef(openRequest);
  useEffect(() => {
    if (openRequest === lastHandledOpenRequest.current) return;
    lastHandledOpenRequest.current = openRequest;
    if (variant !== 'compact' || !isAdmin || isLocationSwitching) return;
    setModalVisible(true);
    requestAnimationFrame(() => bottomSheetRef.current?.present?.());
  }, [openRequest, variant, isAdmin, isLocationSwitching]);

  const clearOwnerState = () => clearOwnerLocationState(dispatch);
  const clearEmployeeState = () => clearEmployeeLocationState(dispatch);

  const handleSelectAllLocations = async () => {
    setModalVisible(false);
    bottomSheetRef.current?.dismiss();

    // If already in all locations mode, do nothing
    if (isAllLocations) {
      return;
    }

    dispatch(setLocationSwitching(true));

    try {
      // Clear backoffice state first
      clearOwnerState();

      // Wait for React to process the clear actions
      await new Promise(resolve => setTimeout(resolve, 150));

      // Set the flag (this also increments locationVersion which triggers refetches)
      dispatch(setIsAllLocations(true));

      // Wait for screens to start their fetch operations
      await new Promise(resolve => setTimeout(resolve, 300));
    } finally {
      dispatch(setLocationSwitching(false));
    }
  };

  const handleLocationSelect = async (locationId?: string) => {
    // Check if user has permission to switch locations
    if (!isEmployeeAdminRole(employeedata?.bp_employee_role)) {
      setModalVisible(false);
      bottomSheetRef.current?.dismiss();
      handleError(
        'You do not have permission to switch locations.',
        'error',
        true,
      );
      return;
    }

    if (!locationId) {
      setModalVisible(false);
      bottomSheetRef.current?.dismiss();
      AppAlert.alert('Error', 'Invalid location selected.');
      return;
    }
    // Check if trying to switch to the same location (only if not in all locations mode)
    if (
      !isAllLocations &&
      currentLocationId &&
      locationId === currentLocationId
    ) {
      setModalVisible(false);
      bottomSheetRef.current?.dismiss();
      AppAlert.alert('Same Location', 'You are already in this location.', [
        { text: 'OK' },
      ]);
      return;
    }

    try {
      setModalVisible(false);
      bottomSheetRef.current?.dismiss();

      // Turn off all locations mode if it was on
      if (isAllLocations) {
        dispatch(setIsAllLocations(false));
      }

      await switchLocation(locationId);

      // Clear state tied to previous location
      clearOwnerState();
      clearEmployeeState();

      // Increment version to trigger refetches
      dispatch(incrementLocationVersion());

      if (onLocationChange) {
        onLocationChange(locationId);
      }

      const newLocation = locations?.find(
        location => location._id === locationId,
      );
      const isTestLocation = !!newLocation?.bp_location_is_system_generated;

      // Show success message and reload app data
      AppAlert.alert(
        isTestLocation ? 'You are on a test location' : 'Location Changed',
        isTestLocation
          ? `You are now on ${newLocation?.bp_location_name}, a test location. This is a safe place to explore BizPlus POS — it does not affect your real business data.`
          : `Location switched successfully. ${newLocation ? `You are now on ${newLocation?.bp_location_name}` : ''} `,
        [
          {
            text: 'OK',
            onPress: () => {
              // Trigger app reload/refresh logic
              if (onLocationChange) {
                onLocationChange(locationId);
              }
            },
          },
        ],
      );
    } catch (error) {
      AppAlert.alert('Error', 'Failed to switch location. Please try again.');
      console.error('Location switch error:', error);
    }
  };

  if (!isAdmin) {
    return null;
  }

  const isCompact = variant === 'compact';
  const locationLabel = formatHeaderLocationName(
    isAllLocations && showAllLocationsOption
      ? 'All Locations'
      : currentLocationData?.bp_location_name || 'Select Location',
    { isWeb: Platform.OS === 'web', picker: 'pos' },
  );

  return (
    <View style={[styles.container, isCompact && styles.containerCompact]}>
      <TouchableOpacity
        style={[styles.selector, isCompact && styles.selectorCompact]}
        onPress={() => {
          setModalVisible(true);
          requestAnimationFrame(() => bottomSheetRef.current?.present?.());
        }}
        disabled={isLocationSwitching}>
        {/* <FontAwesome6
          name={getLocationIcon(currentLocationData?.bp_location_type)}
          size={iconSize(16)}
          color={isCompact ? textColor || '#fff' : '#1560bd'}
        /> */}
        {isAllLocations && showAllLocationsOption && (
          <FontAwesome6
            name="layer-group"
            iconStyle="solid"
            size={iconSize(14)}
            color={isCompact ? textColor || '#fff' : '#1560bd'}
            style={{ marginRight: s(4) }}
          />
        )}
        <Text
          style={[
            // styles.currentLocationText,
            isCompact && {
              color: textColor || '#fff',
              fontSize: fs(locationLabel.fontSize),
            },
          ]}
          numberOfLines={1}>
          {locationLabel.text}
        </Text>
        {isLocationSwitching ? (
          <ActivityIndicator
            size="small"
            color={isCompact ? textColor || '#fff' : '#1560bd'}
          />
        ) : (
          <FontAwesome6
            name="chevron-down"
            iconStyle="solid"
            size={iconSize(12)}
            color={isCompact ? textColor || '#fff' : '#1560bd'}
          />
        )}
      </TouchableOpacity>
      <LocationBottomSheet
        bottomSheetRef={bottomSheetRef}
        onClose={() => setModalVisible(false)}
        footer={
          isEmployeeAdminRole(employeedata?.bp_employee_role) ? (
            <LocationSheetFooter
              onAddBusiness={() => {
                setModalVisible(false);
                bottomSheetRef.current?.dismiss();
                navigation.navigate('NewLocation');
              }}
            />
          ) : null
        }>
        <LocationSheetList
          locations={locations ?? []}
          currentLocationId={currentLocationId}
          isAllLocations={isAllLocations}
          showAllLocationsOption={showAllLocationsOption}
          onSelectLocation={handleLocationSelect}
          onSelectAllLocations={handleSelectAllLocations}
        />
      </LocationBottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: v(15),
  },
  containerCompact: {
    marginBottom: 0,
  },
  selector: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    paddingHorizontal: s(15),
    paddingVertical: s(10),
    borderRadius: s(8),
    borderWidth: borderWidth(2),
    borderColor: 'rgba(21, 96, 189, 0.5)',
    gap: s(8),
  },
  selectorCompact: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    paddingHorizontal: 0,
    paddingVertical: s(2),
    gap: s(6),
  },
  currentLocationText: {
    flex: 1,
    fontSize: fs(16),
    lineHeight: fs(20),
    fontFamily: GTFonts.regular,
    fontWeight: '600',
    color: '#1560bd',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: s(15),
    padding: s(20),
    width: '90%',
    maxHeight: '70%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: v(20),
    paddingBottom: v(15),
    borderBottomWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
    borderBottomColor: '#eee',
  },
  modalTitle: {
    fontSize: fs(20),
    fontFamily: GTFonts.regular,
    fontWeight: 'bold',
    color: '#333',
  },
  closeButton: {
    padding: s(5),
  },
});

export default LocationPicker;
